/**
 * Project roles and what each one may do.
 *
 * Since the backend release of 11/09 the rules below are enforced server side
 * (`capabilities.py`): an action the role does not carry answers 403, and the
 * reads stay open to every member (a non member gets 404, never 403).
 *
 * So this table is no longer *the* permission system — it is the client side
 * mirror of it. Its only job is to keep the UI honest: a control that would
 * answer 403 is never rendered, and the few requests that still slip through
 * (a role changed in another tab) are caught by handleActionError.
 *
 * Keep it in sync with `capabilities.py`. Anything not listed here is allowed
 * to every member, because that is what the backend does.
 *
 * Read a role with the useProjectRole hook; the invitation modal writes one.
 */

export const PROJECT_ROLES = {
    CUSTOMER: 'CUSTOMER',
    MUSICIAN: 'MUSICIAN',
    DIRECTOR: 'DIRECTOR',
    PRODUCER: 'PRODUCER',
};

/** Shown next to an author name. */
export const ROLE_LABELS = {
    CUSTOMER: 'Committente',
    MUSICIAN: 'Musicista',
    DIRECTOR: 'Direttore',
    PRODUCER: 'Produttore',
};

/**
 * Capabilities, as the backend enforces them:
 *
 *   comment            write a message or a reply in the project feed
 *   resolveAnyThread   close a thread opened by somebody else
 *   resolveOwnThread   close a thread one opened oneself
 *   generateActivity   turn a message into a draft activity (LLM)
 *   assignAnyone       confirm a draft onto any member of the team
 *   selfAssign         confirm a draft onto oneself only
 *   editAnyDraft       edit the body / assignee of any draft
 *   editOwnDraft       edit only the drafts one generated oneself
 *   approveActivity    close an assigned or reviewed activity as completed
 *   reviewAnyActivity  send somebody else's activity into review
 *                      (the assignee can always send their own)
 *   editBrief          edit the project brief
 *   manageTeam         invite members and set their role
 */
const CAPABILITIES = {
    PRODUCER: {
        comment: true,
        resolveAnyThread: true,
        resolveOwnThread: true,
        generateActivity: true,
        assignAnyone: true,
        selfAssign: true,
        editAnyDraft: true,
        editOwnDraft: true,
        approveActivity: true,
        reviewAnyActivity: true,
        editBrief: true,
        manageTeam: true,
    },
    DIRECTOR: {
        comment: true,
        resolveAnyThread: true,
        resolveOwnThread: true,
        generateActivity: true,
        assignAnyone: true,
        selfAssign: true,
        editAnyDraft: true,
        editOwnDraft: true,
        approveActivity: true,
        reviewAnyActivity: true,
        editBrief: true,
        manageTeam: true,
    },
    MUSICIAN: {
        comment: true,
        resolveAnyThread: false,
        resolveOwnThread: true,
        generateActivity: true,
        assignAnyone: false,
        selfAssign: true,
        editAnyDraft: false,
        editOwnDraft: true,
        approveActivity: false,
        reviewAnyActivity: false,
        editBrief: false,
        manageTeam: false,
    },
    CUSTOMER: {
        comment: true,
        resolveAnyThread: false,
        resolveOwnThread: true,
        generateActivity: false,
        assignAnyone: false,
        selfAssign: false,
        editAnyDraft: false,
        editOwnDraft: false,
        approveActivity: false,
        reviewAnyActivity: false,
        editBrief: false,
        manageTeam: false,
    },
};

/** Everything off: used while the role is still unknown or outside a project. */
export const NO_CAPABILITIES = Object.keys(CAPABILITIES.PRODUCER)
    .reduce((acc, key) => ({ ...acc, [key]: false }), {});

export const capabilitiesForRole = (role) => CAPABILITIES[role] || NO_CAPABILITIES;

/** Statuses an activity can be approved from. */
const APPROVABLE_STATUSES = ['assigned', 'in_review'];

/**
 * The capability table bound to one user inside one project.
 *
 * Half of the rules are not about the role alone but about the role *and* the
 * object — "resolve only the threads you opened", "edit only the drafts you
 * generated", "send your own task into review". Those are the predicates; the
 * plain flags of the table are spread in as they are.
 *
 * @param   {string|null} role
 * @param   {string|null} currentUserId
 * @returns {object} capability flags + object scoped predicates
 */
export const makeProjectPolicy = ({ role = null, currentUserId = null } = {}) => {
    const flags = capabilitiesForRole(role);
    const isMe = (userId) => Boolean(currentUserId) && userId === currentUserId;

    return {
        ...flags,
        role,

        /** Close a thread. Everybody may close their own. */
        resolveThread: (comment) => Boolean(
            flags.resolveAnyThread
            || (flags.resolveOwnThread && isMe(comment?.author?.userId ?? comment?.authorUserId))
        ),

        /** Edit a draft before it is confirmed. A musician only edits their own. */
        editDraft: (draft) => Boolean(
            flags.editAnyDraft || (flags.editOwnDraft && isMe(draft?.createdBy))
        ),

        /** Confirm a draft at all — onto anybody, or at least onto oneself. */
        confirmDraft: Boolean(flags.assignAnyone || flags.selfAssign),

        /** Hand a task to a given member. */
        assignTo: (userId) => Boolean(flags.assignAnyone || (flags.selfAssign && isMe(userId))),

        /** Members this user may pick as the assignee of a draft. */
        assignableMembers: (team = []) => {
            const accepted = (team || []).filter(member => member.userId && member.status === 'ACCEPTED');

            return flags.assignAnyone
                ? accepted
                : accepted.filter(member => isMe(member.userId));
        },

        /** assigned | in_review -> completed. */
        approveActivityNow: (activity) => Boolean(
            flags.approveActivity && APPROVABLE_STATUSES.includes(activity?.status)
        ),

        /** assigned -> in_review, by the assignee or by a producer / director. */
        submitForReview: (activity) => Boolean(
            activity?.status === 'assigned'
            && (flags.reviewAnyActivity || isMe(activity?.assigneeUserId))
        ),
    };
}

/** A policy that allows nothing: outside a project, or while the role loads. */
export const EMPTY_POLICY = makeProjectPolicy();

export default CAPABILITIES;
